/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { Modal, Spin, Table } from 'antd';
import { Screen } from '../../components';
import { useGetImpugnatedQuestions, useRejectImpugnation } from '../../services/impugnatedQuestion';
import { getColumns } from './constants';
import { type IImpugnatedQuestion } from '../../types/impugnatedquestion';
import { useState } from 'react';
import { QuestionsModal } from '../AdminQuestions/components/QuestionsModal';
import { useGetSubjectsByBranch } from '../../services/subject';
import { useGetTopics } from '../../services/topic';
import clsx from 'clsx';
import { useTheme } from '../../contexts/ThemeContext';
import { IconAlertTriangle } from '@tabler/icons-react';
import './ImpugnatedQuestionsPage.scss';

const ImpugnatedQuestionsPage = () => {
  const { theme } = useTheme();
  const [editingQuestion, setEditingQuestion] = useState<IImpugnatedQuestion>();
  const [sureRemove, setSureRemove] = useState(false);
  const [selectedquestion, setSelectedQuestion] = useState<IImpugnatedQuestion>();

  const { subjects, loading: loadingSubjects } = useGetSubjectsByBranch();
  const { topics, loading: loadingTopics } = useGetTopics();

  const { questions, loading, refetch } = useGetImpugnatedQuestions();

  const { rejectImpugnation, loading: rejecting } = useRejectImpugnation();

  const onAccept = (q: IImpugnatedQuestion) => {
    setEditingQuestion(q);
  };

  const onReject = async (q: IImpugnatedQuestion) => {
    await rejectImpugnation(q._id!);
    refetch();
    setSureRemove(false);
    setSelectedQuestion(undefined);
  };

  const resolveImpugnation = (q: IImpugnatedQuestion) => {
    setSureRemove(true);
    setSelectedQuestion(q);
  };

  const columns = getColumns({ onAccept, onReject: resolveImpugnation });

  return (
    <Screen id="Impugnated" title="Preguntas impugnadas">
      <div className="w-full md:mx-0">
        {(loading || rejecting || loadingSubjects || loadingTopics) ? (
          <div className="flex justify-center items-center min-h-[400px]">
            <Spin tip="Cargando..." size="large" />
          </div>
        ) : (
          <div className={clsx(
            'rounded-xl border-2 overflow-hidden',
            'bg-white border-gray-200 shadow-md',
            'dark:bg-surface-dark dark:border-borders dark:shadow-lg'
          )}>
            <Table
              locale={{ emptyText: 'No hay preguntas impugnadas' }}
              columns={columns}
              dataSource={questions}
              rowKey="_id"
              className={clsx(
                'impugnated-questions-table',
                '[&_.ant-pagination]:mt-4',
                '[&_.ant-pagination]:mb-4',
                // Pagination items
                '[&_.ant-pagination-item]:transition-all',
                '[&_.ant-pagination-item]:border-gray-300',
                '[&_.ant-pagination-item]:dark:border-gray-600',
                '[&_.ant-pagination-item_a]:text-gray-700',
                '[&_.ant-pagination-item_a]:dark:text-gray-300',
                // Active page - more prominent
                '[&_.ant-pagination-item-active]:!bg-blue-600',
                '[&_.ant-pagination-item-active]:!border-blue-600',
                '[&_.ant-pagination-item-active_a]:!text-white',
                '[&_.ant-pagination-item-active]:shadow-md',
                '[&_.ant-pagination-item-active]:shadow-blue-500/30',
                '[&_.ant-pagination-item-active]:dark:!bg-blue-500',
                '[&_.ant-pagination-item-active]:dark:!border-blue-500',
                '[&_.ant-pagination-item-active]:dark:shadow-blue-500/20',
                // Hover states
                '[&_.ant-pagination-item:hover]:border-blue-400',
                '[&_.ant-pagination-item:hover]:dark:border-blue-500',
                '[&_.ant-pagination-item:hover_a]:text-blue-600',
                '[&_.ant-pagination-item:hover_a]:dark:text-blue-400',
                // Navigation buttons
                '[&_.ant-pagination-prev]:text-gray-700',
                '[&_.ant-pagination-prev]:dark:text-gray-300',
                '[&_.ant-pagination-next]:text-gray-700',
                '[&_.ant-pagination-next]:dark:text-gray-300',
                '[&_.ant-pagination-prev:hover]:text-blue-600',
                '[&_.ant-pagination-prev:hover]:dark:text-blue-400',
                '[&_.ant-pagination-next:hover]:text-blue-600',
                '[&_.ant-pagination-next:hover]:dark:text-blue-400',
                // Size changer
                '[&_.ant-pagination-options]:text-gray-700',
                '[&_.ant-pagination-options]:dark:text-gray-300',
                '[&_.ant-pagination-options]:mr-4',
                '[&_.ant-select]:transition-all',
                '[&_.ant-select-selector]:!border-gray-300',
                '[&_.ant-select-selector]:!dark:border-gray-600',
                '[&_.ant-select-selector]:!bg-white',
                '[&_.ant-select-selector]:!dark:bg-surface-dark',
                '[&_.ant-select-selector]:!text-gray-700',
                '[&_.ant-select-selector]:!dark:text-gray-300',
                '[&_.ant-select-selector]:!rounded-lg',
                '[&_.ant-select-selector:hover]:!border-blue-400',
                '[&_.ant-select-selector:hover]:!dark:border-blue-500',
                '[&_.ant-select-focused_.ant-select-selector]:!border-blue-500',
                '[&_.ant-select-focused_.ant-select-selector]:!dark:border-blue-400',
                '[&_.ant-select-focused_.ant-select-selector]:!shadow-md',
                '[&_.ant-select-focused_.ant-select-selector]:!dark:shadow-blue-500/20',
                '[&_.ant-select-arrow]:!text-gray-500',
                '[&_.ant-select-arrow]:!dark:text-gray-400'
              )}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total: ${total} preguntas`,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
            />
          </div>
        )}
      </div>
      {editingQuestion && (
        <QuestionsModal
          topics={topics || []}
          subjects={subjects || []}
          title="Editar pregunta"
          selectedQuestion={editingQuestion?.question}
          open={!!editingQuestion}
          onClose={() => setEditingQuestion(undefined)}
        />
      )}
      {sureRemove && (
        <Modal
          open={sureRemove}
          onCancel={() => setSureRemove(false)}
          okButtonProps={{
            loading: rejecting,
            className: clsx(
              'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
              'border-none shadow-md hover:shadow-lg',
              'transition-all duration-300'
            )
          }}
          cancelButtonProps={{
            className: clsx(
              'border-gray-300 dark:border-gray-600',
              'text-gray-700 dark:text-gray-300',
              'hover:border-gray-400 dark:hover:border-gray-500',
              'transition-all duration-300'
            )
          }}
          onOk={async () => await onReject(selectedquestion!)}
          okText="Resolver impugnación"
          title={
            <div className={clsx(
              'flex items-center gap-3',
              'text-gray-900 dark:text-white'
            )}>
              <div className={clsx(
                'w-10 h-10 rounded-lg flex items-center justify-center',
                'bg-gradient-to-br from-amber-500 to-orange-500',
                'dark:from-amber-600 dark:to-orange-600'
              )}>
                <IconAlertTriangle size={20} className="text-white" />
              </div>
              <span className="text-xl font-bold">Resolver impugnación</span>
            </div>
          }
          centered={false}
          className={clsx(
            'resolve-impugnation-modal',
            theme === 'dark' ? 'dark-modal' : 'light-modal'
          )}
          style={{
            borderRadius: '16px',
            overflow: 'hidden'
          }}
          maskStyle={{
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          wrapClassName={clsx(
            'resolve-impugnation-modal-wrapper',
            theme === 'dark' ? 'dark-modal-wrapper' : 'light-modal-wrapper',
            'bottom-sheet-mobile'
          )}
        >
          <p className={clsx(
            'text-gray-600 dark:text-gray-300'
          )}>
            {'Vas a resolver esta impugnación. Esto la eliminará de esta pantalla y dará por concluida la modificación de la pregunta enviando el resultado de esta al usuario que la impugnó. ¿Estás seguro de que quieres continuar?'}
          </p>
        </Modal>
      )}
    </Screen>
  );
};

export { ImpugnatedQuestionsPage };
